import React, { useId, useMemo } from 'react';
import { Upload, X, Plus } from 'lucide-react';
import { Button } from './ui/button';

function fileKey(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}

/**
 * Multi-select file queue with previews. Supports repeated picks to append files
 * from different folders (NIST 1:N / M×N gallery).
 */
export default function MultiFileUpload({
  files = [],
  onChange,
  accept,
  multiple = true,
  maxFiles,
  label = 'Upload images',
  hint,
  inputId: inputIdProp,
  addMoreLabel = 'Add more files',
}) {
  const autoId = useId();
  const inputId = inputIdProp || `nist-multi-${autoId.replace(/:/g, '')}`;

  const previews = useMemo(
    () =>
      files.map((file) => ({
        file,
        url: file.type?.startsWith('image/') ? URL.createObjectURL(file) : null,
      })),
    [files]
  );

  const mergeFiles = (picked) => {
    if (!picked.length) return files;
    const seen = new Set(files.map(fileKey));
    const merged = [...files];
    for (const file of picked) {
      const key = fileKey(file);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(file);
    }
    let next = multiple ? merged : [picked[0]];
    if (maxFiles) next = next.slice(0, maxFiles);
    return next;
  };

  const handleSelect = (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    onChange(mergeFiles(picked));
    e.target.value = '';
  };

  const removeAt = (index) => {
    onChange(files.filter((_, i) => i !== index));
  };

  const defaultHint =
    hint ||
    (multiple
      ? 'Select one or more files; click again to add more from another folder'
      : 'Select a file');

  return (
    <div className="space-y-3">
      <div className="upload-zone">
        <input
          type="file"
          id={inputId}
          accept={accept}
          multiple={multiple}
          onChange={handleSelect}
          className="hidden"
        />
        <label htmlFor={inputId} className="cursor-pointer block text-center">
          <Upload className="h-10 w-10 mx-auto text-gray-400 mb-2" />
          <p className="text-sm text-gray-600">{label}</p>
          <p className="text-xs text-gray-400 mt-1">{defaultHint}</p>
        </label>
      </div>

      {files.length > 0 && multiple && (
        <div className="flex justify-center">
          <label
            htmlFor={inputId}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background hover:bg-accent hover:text-accent-foreground h-9 px-3 text-sm cursor-pointer"
          >
            <Plus className="h-4 w-4 mr-1" />
            {addMoreLabel}
          </label>
        </div>
      )}

      {files.length > 0 && (
        <ul className="space-y-2">
          {previews.map(({ file, url }, index) => (
            <li
              key={`${fileKey(file)}-${index}`}
              className="flex items-center gap-3 p-2 border rounded-md bg-gray-50"
            >
              {url ? (
                <img src={url} alt="" className="w-12 h-12 object-cover rounded" />
              ) : (
                <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center text-xs text-gray-500">
                  .bin
                </div>
              )}
              <span className="flex-1 text-xs text-gray-700 truncate">{file.name}</span>
              <Button type="button" variant="ghost" size="sm" onClick={() => removeAt(index)} aria-label="Remove">
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
